import { supabase } from "./supabase";
import * as db from "./db";
import { erEkteProduksjon } from "./session";
import * as lager from "./dokumentlager";

/**
 * Dokumenter: henting i feltappen.
 *
 * Appen LESER bare. Opplasting og versjonering hoerer til nettflata, og det
 * er ikke en avgrensning av bekvemmelighet: regi skal ikke kunne endre en
 * dagsplan hen har faatt utdelt.
 *
 * TO REGLER STYRER HVA SOM VISES, og begge ligger i spoerringa framfor i
 * flata:
 *
 * 1. HOEYESTE VERSJON, ikke nyeste opplasting. En rettelse lastet opp etter
 *    en nyere versjon skal ikke kunne gaa foran den.
 *
 *    ALLE OPPTAKSDAGER hentes, ikke bare dagens — én versjon per dag, den
 *    siste. Tidligere dagsplaner brukes naar timene dobbeltsjekkes i
 *    ettertid, og da er de borte om vi bare beholdt i dag. De forsvinner
 *    med resten paa `end_date`, naar tilgangen til produksjonen er over.
 * 2. `visible_from` i framtida betyr at versjonen IKKE finnes ennaa.
 *    Morgendagens dagsplan lastes ofte opp midt i dagens opptak og skal ikke
 *    forstyrre dem som fortsatt jobber. Se docs/06-modul-planer.md.
 *
 * OFFLINE FOERST. `lesLokalt` svarer fra disk uten aa roere nettet, og den
 * svarer med én gang — en regissoer i kjeller eller skog skal ha dagsplanen
 * sin uten at noe maa lastes ned. `synkroniser` kjoeres etterpaa og henter
 * bare det som faktisk er nytt.
 *
 * Fila ligger i OPFS, registeret i SQLite. Se state/dokumentlager.ts for
 * hvorfor delingen er slik, og db.ts for hvordan begge forsvinner paa
 * `end_date`.
 */

const BOETTE = "dokumenter";

export interface HentetDokument {
  readonly serieId: string;
  readonly slag: string;
  readonly tittel: string;
  readonly opptaksdag: string;
  /** Datoen opptaksdagen faller paa, eller tom. Vises paa lista under. */
  readonly dato: string;
  readonly versjon: number;
  readonly filsti: string;
  readonly lastetOpp: string;
  /** Dokumentraden paa serveren. */
  readonly dokumentId: string;
}

/**
 * Dokumentene som er synlige for opptaksdagen akkurat naa.
 *
 * Returnerer en tom liste naar det ikke finnes noen klient. Appen skal kunne
 * brukes uten konto og uten nett — en manglende forbindelse er en normal
 * tilstand her, ikke en feil.
 */
export async function hentDokumenter(
  produksjonId: string,
): Promise<HentetDokument[]> {
  if (supabase === null) return [];

  const [{ data, error }, dager] = await Promise.all([
    supabase
      .from("document_series")
      .select("id, kind, title, shoot_day,"
        + " document(id, version, file_path, uploaded_at)")
      .eq("production_id", produksjonId),
    datoPerDag(produksjonId),
  ]);

  if (error !== null || data === null) return [];

  const ut: HentetDokument[] = [];

  /* `Promise.all` mister den utledede radtypen — supabase-js kan ikke
     se gjennom en tuppel. Formen er den samme som i spoerringen over. */
  const rader = data as unknown as readonly {
    id: string; kind: string; title: string; shoot_day: string | null;
    document: readonly {
      id: string; version: number; file_path: string; uploaded_at: string;
    }[] | null;
  }[];

  for (const rad of rader) {
    const versjoner = rad.document ?? [];
    /*
     * ========  ALT ER LESBART FRA SEKUNDET DET LASTES OPP  ========
     *
     * Her sto et filter paa `visible_from`, og det SKJULTE morgendagens
     * dagsplan til klokkeslettet passerte. Brukeren 02.11.2026:
     * «Dagsplanen skal kunne leses fra sekundet den lastes opp av andre
     * brukere, men den skal ikke faa hovedplasseringen i feltet i appen
     * foer tiden paa forrige dags dagsplan er over.»
     *
     * Det er to ulike spoersmaal, og bare det andre hoerer til tiden:
     * PLASSERINGEN avgjoeres av hvilken opptaksdag appen staar i — se
     * `dagFor` i state/session.ts — og dagen ruller naar gaarsdagens
     * vindu er over, eller ved midnatt om ingen har satt et. Dagens
     * dokument faar hovedplassen; resten staar i lista under, med
     * datoen sin.
     */
    if (versjoner.length === 0) continue;

    const siste = versjoner.reduce((best, v) => (v.version > best.version ? v : best));
    const dag = String(rad.shoot_day ?? "");
    ut.push({
      serieId: String(rad.id),
      slag: String(rad.kind),
      tittel: String(rad.title),
      opptaksdag: dag,
      dato: dager.get(dag) ?? "",
      versjon: siste.version,
      filsti: siste.file_path,
      lastetOpp: siste.uploaded_at,
      dokumentId: siste.id,
    });
  }
  return ut;
}

/** Datoen hver opptaksdag faller paa. Lista under viser den, ikke dagnummeret. */
async function datoPerDag(produksjonId: string): Promise<Map<string, string>> {
  const ut = new Map<string, string>();
  if (supabase === null) return ut;
  const { data } = await supabase
    .from("shoot_day")
    .select("day, date")
    .eq("production_id", produksjonId);
  for (const r of (data ?? []) as readonly Record<string, unknown>[]) {
    ut.set(String(r["day"]), String(r["date"]));
  }
  return ut;
}

/**
 * Henter sjoelve fila og gir en URL appen kan vise.
 *
 * Boetta er privat, saa dette gaar gjennom en policy og ikke gjennom en
 * gjettbar adresse. Resultatet er en blob-URL: den lever i denne fana og kan
 * ikke deles videre, hvilket er samme krav som gjelder dokumentvisningen
 * ellers — ingen delingsknapp, ingen «aapne i …».
 */
export async function hentFil(filsti: string): Promise<string | null> {
  if (supabase === null) return null;
  const { data, error } = await supabase.storage.from(BOETTE).download(filsti);
  if (error !== null || data === null) return null;
  return URL.createObjectURL(data);
}


/* ---- offline ------------------------------------------------------------ */

export interface LokaltDokument {
  readonly serieId: string;
  readonly slag: string;
  readonly tittel: string;
  readonly opptaksdag: string;
  /** Datoen opptaksdagen faller paa, eller tom. Merket i lista under. */
  readonly dato: string;
  readonly versjon: number;
  readonly lastetOpp: string;
  /** Dokumentraden paa serveren. Lesekvitteringen henger paa den. */
  readonly dokumentId: string | null;
  /** Ikke aapnet av DEG. Se `kvitterLest`. */
  readonly ulest: boolean;
  /** Blob-URL til fila paa disk, eller null naar den ikke lot seg lese. */
  readonly url: string | null;
  /** Sant naar vi VET at det finnes en nyere versjon vi ikke har hentet. */
  readonly foreldet: boolean;
}

/**
 * Det som ligger nedlastet. Roerer ikke nettet.
 *
 * Kalles foerst, alltid. Returnerer tom liste naar ingenting er lastet ned —
 * som er riktig foerste gang appen aapnes, og ikke en feil.
 */
export async function lesLokalt(produksjonId: string): Promise<LokaltDokument[]> {
  /*
   * Basen maa vaere aapnet foerst.
   *
   * `openDb` er memoisert, saa dette venter paa migreringen som alt er i
   * gang framfor aa starte en ny. Uten det spurte vi etter `document_cache`
   * foer tabellen fanns, og hele flata falt — SessionProvider aapner basen i
   * sin egen effekt, og det er ingen garanti for at den er ferdig naar denne
   * kjoerer.
   */
  await db.openDb(produksjonId);
  const rader = await db.lagredeDokumenter(produksjonId);
  const ut: LokaltDokument[] = [];
  for (const r of rader) {
    ut.push({
      serieId: r.serieId,
      slag: r.slag,
      tittel: r.tittel,
      opptaksdag: r.opptaksdag,
      dato: r.dato,
      versjon: r.versjon,
      lastetOpp: r.lastetOpp,
      dokumentId: r.dokumentId,
      ulest: r.ulest,
      url: await lager.les(r.filnavn),
      foreldet: false,
    });
  }
  return ut;
}

export interface Synk {
  /** Serier som fikk en ny versjon. Tomt naar ingenting endret seg. */
  readonly oppdaterte: readonly string[];
  /** Sant naar serveren ikke svarte. Da staar det lokale, og det er riktig. */
  readonly utenNett: boolean;
}

/**
 * Henter det som er nytt, og bare det.
 *
 * EN VERSJON LASTES NED ÉN GANG. Finnes den alt lokalt, roeres den ikke —
 * og den gamle fila slettes foerst naar den nye er skrevet. Rekkefoelgen er
 * med vilje: avbrytes nedlastingen, har man fortsatt den forrige versjonen
 * framfor ingenting.
 */
export async function synkroniser(produksjonId: string): Promise<Synk> {
  if (supabase === null) return { oppdaterte: [], utenNett: true };
  /* En privat logg hoerer ingen produksjon til, og har ingen dokumenter aa
     hente. `utenNett: false` — dette er ikke et nettproblem, saa det skal
     ikke utloese en ny henting om tjue sekunder. */
  if (!erEkteProduksjon(produksjonId)) return { oppdaterte: [], utenNett: false };
  await db.openDb(produksjonId);

  let fjerne: HentetDokument[];
  try {
    fjerne = await hentDokumenter(produksjonId);
  } catch {
    return { oppdaterte: [], utenNett: true };
  }
  if (fjerne.length === 0) return { oppdaterte: [], utenNett: false };

  const lokale = new Map(
    (await db.lagredeDokumenter(produksjonId)).map((d) => [d.serieId, d]));
  const oppdaterte: string[] = [];

  for (const f of fjerne) {
    const har = lokale.get(f.serieId);
    if (har !== undefined && har.versjon >= f.versjon) continue;

    const url = await hentFil(f.filsti);
    if (url === null) continue;
    let data: Blob;
    try {
      data = await (await fetch(url)).blob();
    } finally {
      URL.revokeObjectURL(url);
    }

    const navn = lager.filnavnFor(f.serieId, f.versjon);
    if (!(await lager.lagre(navn, data))) continue;

    await db.lagreDokument(produksjonId, {
      serieId: f.serieId, slag: f.slag, tittel: f.tittel, opptaksdag: f.opptaksdag,
      dato: f.dato,
      versjon: f.versjon, lastetOpp: f.lastetOpp, filnavn: navn,
      dokumentId: f.dokumentId,
      /*
       * ULEST FORDI DEN ER NY HER, IKKE FORDI SERVEREN SIER DET.
       *
       * Brukeren 02.11.2026: «Vi trenger ikke aa overvaake hvem som har
       * lest et dokument og ikke. Dette er ikke nord-korea.»
       *
       * Hen har rett, og merket trenger ikke overvaakingen: det er
       * TELEFONEN som vet hva den har vist deg. En versjon som nettopp
       * ble hentet ned er ulest her, og `markerLest` toemmer merket naar
       * du aapner den. Ingen rad forlater enheten, og ingen annen kan se
       * hva du har lest.
       */
      ulest: true,
    });

    // Den gamle fila slettes FOERST naa — etter at den nye er paa plass og
    // registrert. CLAUDE.md: «Lokal kopi slettes naar ny versjon lastes ned.»
    if (har !== undefined) await lager.slett(har.filnavn);
    oppdaterte.push(f.serieId);
  }
  return { oppdaterte, utenNett: false };
}
